// Audio file library: upload once, pick from the list everywhere a sound file is needed
// (IVR prompts, queue music/announcement, broadcast audio, inbound-route PLAYBACK destination).
import * as React from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Upload, Trash2, Play, Pencil } from 'lucide-react';
import { api, blobUrl, errorText } from '@/lib/api';
import { PageHeader, DataTable, ErrorBox, Field } from '@/components/common';
import { Button } from '@/components/ui/button';
import { Input, Select } from '@/components/ui/input';
import { Dialog, DialogContent } from '@/components/ui/dialog';
import { useCan } from '@/store/auth';
import { toast } from '@/store/toast';
import { fmtTime, enumLabel } from '@/lib/utils';
import { t } from '@/i18n';

const CATEGORIES = ['MOH', 'IVR_PROMPT', 'ANNOUNCEMENT', 'BROADCAST', 'OTHER'];

function AudioPlayer({ id }: { id: number }) {
  const [url, setUrl] = React.useState<string | null>(null);
  React.useEffect(() => () => { if (url) URL.revokeObjectURL(url); }, [url]);
  if (url) return <audio src={url} controls autoPlay className="h-8" />;
  return <Button size="sm" variant="outline" onClick={() => blobUrl(`/audio-files/${id}/file`).then(setUrl).catch((e) => toast.error(errorText(e)))}><Play /> {t('Play')}</Button>;
}

// a file's voice language and the file it is a recording of in that language (PHASE26)
function LanguageFields({ language, base, files, self, onLanguage, onBase }: {
  language: string; base: string; files: any[]; self?: number; onLanguage: (v: string) => void; onBase: (v: string) => void;
}) {
  return (<>
    <Field help="audio.language" label={t('Voice language')} hint={t('The language spoken in this file, e.g. en or es. Empty: the voice default language.')}>
      <Input name="language" value={language} onChange={(e) => onLanguage(e.target.value.trim())} placeholder="en, es, …" maxLength={6} />
    </Field>
    <Field help="audio.base_file" label={t('Recording of')} hint={t('Pick the file this one says in another language. Callers in that language then hear this file wherever the other one is used.')}>
      <Select name="base_file_id" value={base} onChange={(e) => onBase(e.target.value)}>
        <option value="">{t('(none: a file of its own)')}</option>
        {files.filter((f) => !f.base_file_id && f.id !== self).map((f) => <option key={f.id} value={f.id}>{f.name}{f.language ? ` (${f.language})` : ''}</option>)}
      </Select>
    </Field>
  </>);
}

function EditForm({ file, files, onDone }: { file: any; files: any[]; onDone: () => void }) {
  const [language, setLanguage] = React.useState<string>(file.language ?? '');
  const [base, setBase] = React.useState<string>(file.base_file_id ? String(file.base_file_id) : '');
  const [error, setError] = React.useState<unknown>(null);
  async function save() {
    setError(null);
    try {
      await api(`/audio-files/${file.id}`, { method: 'PATCH', body: { language: language || null, base_file_id: base ? Number(base) : null } });
      toast.ok(t('Saved')); onDone();
    } catch (e) { setError(e); }
  }
  return (
    <div className="grid gap-3">
      <LanguageFields language={language} base={base} files={files} self={file.id} onLanguage={setLanguage} onBase={setBase} />
      <ErrorBox error={error} />
      <div className="flex justify-end gap-2"><Button variant="outline" onClick={onDone}>{t('Cancel')}</Button><Button onClick={save}>{t('Save')}</Button></div>
    </div>
  );
}

function UploadForm({ files, onDone }: { files: any[]; onDone: () => void }) {
  const [file, setFile] = React.useState<File | null>(null);
  const [name, setName] = React.useState('');
  const [category, setCategory] = React.useState('OTHER');
  const [language, setLanguage] = React.useState('');
  const [base, setBase] = React.useState('');
  const [error, setError] = React.useState<unknown>(null);
  const [busy, setBusy] = React.useState(false);
  async function go() {
    if (!file) return;
    setError(null); setBusy(true);
    try {
      const fd = new FormData();
      // text fields before the file: the server reads the multipart stream in order
      fd.append('name', name || file.name);
      fd.append('category', category);
      if (language) fd.append('language', language);
      if (base) fd.append('base_file_id', base);
      fd.append('file', file);
      await api('/audio-files', { raw: fd });
      toast.ok(t('Uploaded'));
      onDone();
    } catch (e) { setError(e); } finally { setBusy(false); }
  }
  return (
    <div className="grid gap-3">
      <Input type="file" accept=".wav,.mp3,audio/wav,audio/mpeg" onChange={(e) => { const f = e.target.files?.[0] ?? null; setFile(f); if (f && !name) setName(f.name.replace(/\.[^.]+$/, '')); }} />
      <Input placeholder={t('Name')} value={name} onChange={(e) => setName(e.target.value)} />
      <Select name="category" value={category} onChange={(e) => setCategory(e.target.value)}>{CATEGORIES.map((c) => <option key={c} value={c}>{enumLabel(c)}</option>)}</Select>
      <LanguageFields language={language} base={base} files={files} onLanguage={setLanguage} onBase={setBase} />
      <ErrorBox error={error} />
      <div className="flex justify-end gap-2"><Button variant="outline" onClick={onDone}>{t('Cancel')}</Button><Button onClick={go} disabled={!file || busy}>{t('Upload')}</Button></div>
    </div>
  );
}

export function AudioLibraryPage() {
  const qc = useQueryClient();
  const [uploading, setUploading] = React.useState(false);
  const [editing, setEditing] = React.useState<any>(null);
  const [canAdd, canDelete] = [useCan('audio.add'), useCan('audio.delete')];
  const rows = useQuery({ queryKey: ['ref', 'audio-files'], queryFn: () => api<{ rows: any[] }>('/audio-files') });
  async function remove(r: any) {
    if (!confirm(t('Delete "{{name}}"? Anything still referencing this file will fail to play.', { name: r.name }))) return;
    try { await api(`/audio-files/${r.id}`, { method: 'DELETE' }); toast.ok(t('Deleted')); rows.refetch(); }
    catch (e) { toast.error(errorText(e)); }
  }
  const all = rows.data?.rows ?? [];
  const nameOf = (id: number) => all.find((f) => f.id === id)?.name ?? `#${id}`;
  function uploaded() { setUploading(false); setEditing(null); rows.refetch(); qc.invalidateQueries({ queryKey: ['ref', 'audio-files'] }); }
  return (
    <div>
      <PageHeader title={t('Audio Library')} description={t('Upload prompts, music-on-hold, announcements and broadcast audio once; pick them by name wherever a sound file is needed.')}>
        {canAdd && <Button onClick={() => setUploading(true)}><Upload /> {t('Upload')}</Button>}
      </PageHeader>
      <ErrorBox error={rows.error} />
      <DataTable rows={rows.data?.rows} empty={t('No audio files uploaded yet.')} columns={[
        { key: 'name', label: t('Name') }, { key: 'category', label: t('Category'), render: (r) => enumLabel(r.category) },
        { key: 'language', label: t('Voice language'), render: (r) => r.language ?? '' },
        { key: 'base_file_id', label: t('Recording of'), render: (r) => r.base_file_id ? nameOf(r.base_file_id) : '' },
        { key: 'size_bytes', label: t('Size'), render: (r) => r.size_bytes ? `${Math.round(r.size_bytes / 1024)} KB` : '-' },
        { key: 'created_at', label: t('Uploaded'), render: (r) => fmtTime(r.created_at) }]}
        actions={(r) => <div className="flex justify-end gap-1"><AudioPlayer id={r.id} />{canAdd && <Button size="icon" variant="ghost" aria-label={t('Edit {{name}}', { name: r.name })} onClick={() => setEditing(r)}><Pencil /></Button>}{canDelete && <Button size="icon" variant="ghost" aria-label={t('Delete')} onClick={() => remove(r)}><Trash2 className="text-destructive" /></Button>}</div>} />
      <Dialog open={uploading} onOpenChange={setUploading}>
        {uploading && <DialogContent title={t('Upload an audio file')}><UploadForm files={all} onDone={uploaded} /></DialogContent>}
      </Dialog>
      <Dialog open={!!editing} onOpenChange={(o) => !o && setEditing(null)}>
        {editing && <DialogContent title={t('Edit {{name}}', { name: editing.name })}><EditForm file={editing} files={all} onDone={uploaded} /></DialogContent>}
      </Dialog>
    </div>
  );
}
